import * as React from "react";
import emailjs from "emailjs-com";
import { useAnimation, motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

export default function ContactUs({ setIsOpen, setIsError }) {
  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [message, setMessage] = React.useState("");

  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const control = useAnimation();
  const [ref, inView] = useInView();

  const variantBuild = (i) => {
    return {
      visible: {
        opacity: 1,
        y: 0,
        transition: { delay: 0.3 * i, duration: 0.5 },
      },
      hidden: { opacity: 0, y: 100 },
    };
  };

  React.useEffect(() => {
    if (inView) {
      control.start("visible");
    } else {
      control.start("hidden");
    }
  }, [control, inView]);

  const formRef = React.useRef(null);

  const clearForm = () => {
    setName("");
    setEmail("");
    setMessage("");
  };

  const sendEmail = (e) => {
    e.preventDefault();

    setIsSubmitting(true);
    emailjs
      .sendForm(
        `${process.env.REACT_APP_SERVICE_ID}`,
        `${process.env.REACT_APP_TEMPLATE_ID}`,
        formRef.current,
        `${process.env.REACT_APP_PUBLIC_KEY}`
      )
      .then(
        (result) => {
          setIsOpen(true);
          clearForm();
          console.log(result.text);
        },
        (error) => {
          // Show modal
          setIsError(true);
          console.log(error.text);
        }
      )
      .finally(() => setIsSubmitting(false));
  };

  return (
    <section
      id="contact-us"
      className="bg-gradient-to-br from-[#114F94] via-[#79B6F6] to-[#397CC4] relative overflow-hidden"
    >
      {/* Desktop Image */}
      <img
        className="absolute hidden md:block left-0 w-1/3 h-full object-cover"
        src="images/BgContactUs.png"
        alt="Contact Us"
      />

      {/* Mobile Image */}
      <img
        className="absolute md:hidden inset-0 w-full h-full object-cover"
        src="images/BgContactUs.png"
        alt="Contact Us"
      />

      <div className="bg-[#114F94]/50 flex flex-col md:flex-row mb-8 py-4 relative z-10" ref={ref}>
        {/* Column 1 */}
        <motion.div
          animate={control}
          initial="hidden"
          variants={variantBuild(0)}
          className="grid place-items-center col-span-2 md:w-1/3"
        >
          <h2 className="text-3xl font-bold text-default">Contacts</h2>
        </motion.div>

        {/* Column 2 */}
        <div className="pt-4 px-8 flex flex-row md:flex-col md:w-1/3 md:gap-8">
          <motion.div
            animate={control}
            initial="hidden"
            variants={variantBuild(1)}
            className="flex gap-2 text-default mb-12"
          >
            <div className="textWhite">
              <svg
                aria-hidden="true"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
                className="w-6 h-6 md:w-8 md:h-8"
              >
                <path
                  d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></path>
              </svg>
            </div>
            <div className="flex flex-col gap-2">
              <p className="font-bold text-xl md:text-2xl">Phone</p>
              <span className="text-xs whitespace-nowrap md:text-[20px] mb-2">+62 21-2962-5790</span>
              <span className="text-xs whitespace-nowrap md:text-[20px] mb-2">+62 21-2962-5791</span>
              <span className="text-xs whitespace-nowrap md:text-[20px] mb-2">+62 81-1811-2559</span>
            </div>
          </motion.div>

          <motion.div
            animate={control}
            initial="hidden"
            variants={variantBuild(2)}
            className="flex gap-2 text-default mb-12"
          >
            <div className="textWhite">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="w-6 h-6 md:w-8 md:h-8"
              >
                <path
                  strokeLinecap="round"
                  d="M16.5 12a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zm0 0c0 1.657 1.007 3 2.25 3S21 13.657 21 12a9 9 0 10-2.636 6.364M16.5 12V8.25"
                />
              </svg>
            </div>
            <div className="flex flex-col gap-2">
              <p className="font-bold text-xl md:text-2xl">Email</p>
              <span
                className="block md:text-[20px] text-sm break-words max-w-[150px] md:max-w-full"
                title="mailto:surya.bintang.cemerlang@gmail.com"
              >
                surya.bintang.cemerlang@gmail.com
              </span>
            </div>
          </motion.div>
        </div>

        {/* Column 3 */}
        <motion.div
          className="flex gap-2 text-default col-span-2 px-8 md:w-1/3"
          animate={control}
          initial="hidden"
          variants={variantBuild(3)}
        >
          <div className="textWhite">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              className="w-6 h-6 md:w-8 md:h-8"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z"
              />
            </svg>
          </div>
          <div className="flex flex-col gap-2">
            <p className="font-bold text-xl md:text-2xl">Address</p>
            <span className="mb-2">
              Thamrin City Building 7th Floor - OS No.20 Jl. Thamrin Boulevard Tanah Abang - Central Jakarta 10340
            </span>
            <div className="mapouter">
              <div className="gmap_canvas">
                <iframe
                  title="map"
                  className="w-full aspect-[520/400]"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3966.5229112774377!2d106.8117158231954!3d-6.194521681171965!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x2e69f69e2ca1776f%3A0x1b60abd2e0b3d6d1!2sThamrin%20City!5e0!3m2!1sen!2sid!4v1674376582713!5m2!1sen!2sid"
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      <div className="bg-[#114F94]/50 flex flex-col md:flex-row pt-4 pb-8 relative z-10" ref={ref}>
        <div className="grid place-items-center col-span-2 md:w-1/3">
          <motion.h2
            animate={control}
            initial="hidden"
            variants={variantBuild(0)}
            className="text-3xl font-bold text-default"
          >
            Contact Us Now
          </motion.h2>
        </div>

        <form ref={formRef} action="" onSubmit={sendEmail} className="flex flex-col md:flex-row p-4 md:gap-4 md:w-2/3">
          <div className="w-full">
            <div className="md:grid grid-cols-4 flex flex-col">
              <motion.div
                animate={control}
                initial="hidden"
                variants={variantBuild(1)}
                className="pt-4 md:pl-8 col-span-2"
              >
                {" "}
                <label htmlFor="name" className="text-default block mb-1">
                  Your Name
                </label>
                <input
                  className="w-full bg-transparent !outline-none rounded-full border-2 borderWhite px-2 py-0.5 text-default"
                  type="text"
                  id="name"
                  name="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  title="What's your name?"
                  required
                />
              </motion.div>

              <motion.div
                animate={control}
                initial="hidden"
                variants={variantBuild(2)}
                className="pt-4 md:pl-8 col-span-2"
              >
                {" "}
                <label htmlFor="email" className="text-default block mb-1">
                  Your Email
                </label>
                <input
                  className="w-full bg-transparent !outline-none rounded-full border-2 borderWhite px-2 py-0.5 text-default"
                  type="email"
                  id="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  title="What's your email?"
                  pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
                  required
                />
              </motion.div>
            </div>

            <motion.div animate={control} initial="hidden" variants={variantBuild(3)} className="pt-4 md:pl-8">
              {" "}
              <label htmlFor="message" className="text-default block mb-1">
                Your Message
              </label>
              <textarea
                className="w-full bg-transparent !outline-none rounded-xl border-2 borderWhite px-2 py-0.5 text-default"
                rows="6"
                cols="50"
                type="text"
                id="message"
                name="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                title="What's your message?"
                required
              />
            </motion.div>
          </div>

          <motion.button
            animate={control}
            initial="hidden"
            variants={variantBuild(4)}
            className="flex flex-col items-center justify-center text-default self-end mt-4 md:mt-0"
          >
            {" "}
            <div className="rounded-full bg-[#114F94] p-2 hover:bg-[#6CB2F9] duration-300 relative">
              {isSubmitting ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  className="w-8 h-8"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                  />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  className="w-8 h-8"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5"
                  />
                </svg>
              )}
            </div>
            Send
          </motion.button>
        </form>
      </div>
    </section>
  );
}
